import { EncryptProps, AESAndGCMProps } from './types';

const forge = window.forge;

/*
Auxiliar function to transform an ArrayBuffer to a hexadecimal string.
*/
export const buf2hex = (buffer: ArrayBuffer): string => {
  return [...new Uint8Array(buffer)]
    .map(x => x.toString(16).padStart(2, '0'))
    .join('');
}

/*
PBKDF2 (Password Based Key Derivation Function 2) used for deriving a cryptographic key from a password.
*/
export const pbkdf2 = async (iterations = 1e6): Promise<string> => {
  const pwUtf8 = crypto.getRandomValues(new Uint8Array(16));
  const pwKey = await crypto.subtle.importKey('raw', pwUtf8, 'PBKDF2', false, ['deriveBits']);

  const saltUint8 = crypto.getRandomValues(new Uint8Array(16));
  const params = { name: 'PBKDF2', hash: { name: 'SHA-256' }, salt: saltUint8, iterations: iterations };
  const keyBuffer = await crypto.subtle.deriveBits(params, pwKey, 8 * 8);

  return buf2hex(keyBuffer);
}

/*
RSA-OAEP algorithm encryption function to encrypt the AESKey.
*/
export const encryptWithRSAAndOAEP = (AESKey: string, publicKey: string,): string => {
  const publicKeyFromPem = forge.pki.publicKeyFromPem(`-----BEGIN RSA PUBLIC KEY-----${publicKey}-----END RSA PUBLIC KEY-----`);

  return publicKeyFromPem.encrypt(AESKey, 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: {
      md: forge.md.sha1.create(),
    }
  });
}

/*
AES-GCM algorithm encryption function to encrypt any kind of data of type string.
*/
export const encryptWithAESAndGCM = (data: string, iv: string, AESKey: string): AESAndGCMProps => {
  const cipher = forge.cipher.createCipher('AES-GCM', AESKey);
  cipher.start({
    iv
  });
  cipher.update(forge.util.createBuffer(data));
  cipher.finish();

  return {
    data: cipher.output.data,
    tag: cipher.mode.tag.toHex()
  };
}

/*
Main encryption function: by passing any data string and the public key to this function it
will encrypt the data and return it together with a IV and the encrypted AESKey.
*/
export const encrypt = async (publicKey: any, data: string): Promise<EncryptProps> => {
  // First of all we need to generate an AES key to be used to encrypt the data
  const AESKey = await pbkdf2();
  const iv = await pbkdf2();

  // The data is encrypted with the AES algorithm, using the generated AESkey and an IV
  const encrypted = encryptWithAESAndGCM(data, iv, AESKey);
  const encryptedAESKey = encryptWithRSAAndOAEP(AESKey, publicKey);
  return {
    data: forge.util.encode64(encrypted.data),
    tag: forge.util.encode64(encrypted.tag),
    iv: forge.util.encode64(iv),
    encAesKey: forge.util.encode64(encryptedAESKey),
  };
}